import {
  generateArrayWithValues,
  randomiseList,
} from '@freelancer/datastore/testing/helpers';
import type { OnlineOfflineUserStatus } from './online-offline.model';
import { OnlineOfflineUserStatusValue } from './online-offline.model';

interface GenerateByPercent {
  /** Ranges from 0-100, the default is 50 */
  readonly percentOnline?: number;
  readonly onlineOfflineStatuses?: never;
}

interface GenerateByStatuses {
  readonly onlineOfflineStatuses: readonly OnlineOfflineUserStatusValue[];
  readonly percentOnline?: never;
}

export type GenerateOnlineOfflineOptions = {
  readonly userIds: readonly number[];
} & (GenerateByPercent | GenerateByStatuses);

export function generateOnlineOfflineObjects(
  options: GenerateOnlineOfflineOptions,
): readonly OnlineOfflineUserStatus[] {
  const { userIds } = options;

  if (
    'onlineOfflineStatuses' in options &&
    options.onlineOfflineStatuses !== undefined
  ) {
    const { onlineOfflineStatuses } = options;
    if (userIds.length !== onlineOfflineStatuses.length) {
      throw new Error(
        `The number of userIds (${userIds.length}) must match the number of onlineOfflineStatuses provided (${onlineOfflineStatuses.length}).`,
      );
    }

    return userIds.map((userId, i) => ({
      id: userId,
      status: onlineOfflineStatuses[i],
    }));
  }

  const percentOnline = options.percentOnline ?? 50;
  const numberOnline = Math.floor((userIds.length * percentOnline) / 100);

  const statuses = randomiseList(
    [
      ...generateArrayWithValues(
        numberOnline,
        OnlineOfflineUserStatusValue.ONLINE,
      ),
      ...generateArrayWithValues(
        userIds.length - numberOnline,
        OnlineOfflineUserStatusValue.OFFLINE,
      ),
    ],
    'onlineOfflineStatuses',
  );

  return userIds.map((userId, i) => ({
    id: userId,
    status: statuses[i],
  }));
}
